import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {enqueueOfflineDraft, getOfflineDrafts, migrateOfflineDrafts, syncOfflineDrafts, OFFLINE_DRAFTS_EVENT} from '../lib/offline/offlineDraftService';
import {shouldSyncOnResume} from '../lib/pwa/pwaSafety';

const Context = createContext(null);

export function OfflineQueueProvider({session, children}) {
  const userId = session?.user?.id;
  const account = useRef(userId); account.current = userId;
  const client = useQueryClient();
  const [drafts, setDrafts] = useState([]), [isOnline, setOnline] = useState(navigator.onLine);
  const [isSyncing, setSyncing] = useState(false), [error, setError] = useState('');
  const activeSync = useRef(null);
  const refreshCount = useCallback(async () => {
    try { const rows = await getOfflineDrafts(userId); if (account.current === userId) setDrafts(rows); }
    catch (e) { if (account.current === userId) setError(e.message); }
  }, [userId]);
  const syncDrafts = useCallback(async () => {
    if (!userId || !navigator.onLine || activeSync.current === userId) return;
    activeSync.current = userId; setSyncing(true); setError('');
    try {
      const result = await syncOfflineDrafts(userId);
      if (account.current === userId && result.synced) await client.invalidateQueries();
      return result;
    } catch (e) { if (account.current === userId) setError(e.message); }
    finally { if (activeSync.current === userId) { activeSync.current = null; setSyncing(false); } await refreshCount(); }
  }, [userId, client, refreshCount]);
  useEffect(() => {
    setDrafts([]); setError('');
    let cancelled = false;
    if (userId) migrateOfflineDrafts(userId).then(async () => { if (!cancelled) { await refreshCount(); await syncDrafts(); } }).catch(e => { if (!cancelled) setError(e.message); });
    const refresh = () => { refreshCount(); };
    const online = () => { setOnline(true); syncDrafts(); };
    const offline = () => setOnline(false);
    const resume = () => {
      const onlineNow = navigator.onLine;
      setOnline(onlineNow);
      refreshCount();
      if (shouldSyncOnResume({userId, online: onlineNow, visibilityState: document.visibilityState})) syncDrafts();
    };
    window.addEventListener(OFFLINE_DRAFTS_EVENT, refresh);
    window.addEventListener('online', online); window.addEventListener('offline', offline);
    window.addEventListener('focus', resume); window.addEventListener('pageshow', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      cancelled = true;
      window.removeEventListener(OFFLINE_DRAFTS_EVENT, refresh);
      window.removeEventListener('online', online); window.removeEventListener('offline', offline);
      window.removeEventListener('focus', resume); window.removeEventListener('pageshow', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [userId, refreshCount, syncDrafts]);
  const saveDraft = useCallback(async (type, data) => {
    if (!userId || data.userId !== userId) throw new Error('Sign in to save this record.');
    await enqueueOfflineDraft(type, data);
    await refreshCount();
    if (navigator.onLine) await syncDrafts();
    const remaining = await getOfflineDrafts(userId);
    return {offline: remaining.some(x => x.id === data.values.submissionId), value: null};
  }, [userId, refreshCount, syncDrafts]);
  return <Context.Provider value={{drafts: drafts.filter(x => x.data.userId === userId), draftCount: drafts.filter(x => x.data.userId === userId).length, isOnline, isSyncing, error, saveDraft, syncDrafts, refreshCount}}>{children}</Context.Provider>;
}

export function useOfflineQueue() {
  const value = useContext(Context);
  if (!value) throw new Error('OfflineQueueProvider is required.');
  return value;
}
